import { lastValueFrom } from "rxjs";
import { createEffect, createResource, createSignal, For } from "solid-js";

import { ContactsEditor } from "./ContactsEditor";
import type { UtdoContact } from "../types";
import { action } from "../util/action";
import { useApi } from "../util/api";
import { panic } from "../util/panic";
import { success } from "../util/success";

export function Contacts() {
    const api = useApi();

    const [ currentId, setCurrentId ] = createSignal<number | undefined>(undefined);
    const [ contacts, { refetch } ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectContacts())
            } catch (err: any) {
                panic(err);
            }
            return [] as UtdoContact[];
        },
        { initialValue: [] as UtdoContact[] },
    );

    const currentContact = (): UtdoContact => {
        const id = currentId();
        return contacts().find((c) => c.id === id) ?? {
            id: 0,
            name: "",
            value: "",
            sort: 0,
            hidden: false,
        };
    };

    let dialogRef!: HTMLDialogElement;

    createEffect(() => {
        const id = currentId();
        if (id === undefined) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    });

    const deleteContact = action(
        async (c: UtdoContact) => {
            if (!confirm(`Vous allez supprimer le contact "${c.name}".\nContinuer ?`)) {
                return;
            }
            await lastValueFrom(api.deleteContact(c.id!));
        },
        refetch,
    );
    const editContact = (id?: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setCurrentId(id);
    };
    const moveDown = action(
        async (c: UtdoContact) => lastValueFrom(api.moveContactDown(c.id!)),
        refetch,
    );
    const moveUp = action(
        async (c: UtdoContact) => lastValueFrom(api.moveContactUp(c.id!)),
        refetch,
    );
    const save = async (contact: UtdoContact) => {
        try {
            if ((contact.id ?? 0) > 0) {
                if (await lastValueFrom(api.updateContact(contact))) {
                    success("Contact enregistré avec succès.");
                }
            } else {
                if (await lastValueFrom(api.insertContact(contact))) {
                    success("Contact enregistré avec succès.");
                }
            }
            setCurrentId(undefined);
            refetch();
        } catch (err: any) {
            panic(err);
        }
    };
    const toggleVisibility = action(
        async (c: UtdoContact) => lastValueFrom(api.toggleContactVisibility(c.id!)),
        refetch,
    );

    return <section id="admin-contacts">
        <h2>Contacts</h2>
        <div>
            <div class="actions">
                <button onClick={editContact(0)}>Nouveau contact</button>
            </div>
            <ul>
                <For each={contacts()}>
                    {(c) => <li>
                        <a href="#"
                            onClick={editContact(c.id!)}>{c.name}</a>
                        <div class="actions">
                            <a href="#" class="icon"
                                onClick={moveUp(c)}>arrow_upward</a>
                            <a href="#" class="icon"
                                onClick={moveDown(c)}>arrow_downward</a>
                            <a href="#" class="icon"
                                onClick={toggleVisibility(c)}>
                                {
                                    c.hidden
                                        ? "visibility_off"
                                        : "visibility"
                                }
                            </a>
                            <a href="#" class="icon"
                                onClick={deleteContact(c)}>delete</a>
                        </div>
                    </li>}
                </For>
            </ul>
        </div>
        <dialog ref={dialogRef}>
            <ContactsEditor
                contact={currentContact()}
                validate={save}
                cancel={() => setCurrentId(undefined)}
                />
        </dialog>
    </section>;
}